/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useTranslation } from "react-i18next";
import { BsPauseFill } from "react-icons/bs";
import { useLocation } from "react-router";
import { useIsMobile } from "~/hook/use-mobile";
import { useFontStyle } from "~/hook/useFontStyle";
import useLocale from "~/hook/useLocale";
import useStopCurrentRunningVideoAudio from "~/hook/useStopCurrentRunningVideoAudio";
import { LiveData } from "~/interface/home/home";
import { removeAudioPlayerKeyFromSession } from "~/lib/helperFuncation/sessionStorageService";
import { getYoutubeVideoThumbnail } from "~/lib/helperFuncation/youtubeThumbnails";
import { addToCurrentAudio, changeIsAudioPlayState, changeIsPlayingState, removeQueue, setMediaTypeControl } from "~/lib/store/slice/audioSlice";
import { onPauseVideo, onPlayVideo } from "~/lib/store/slice/youtubePlayerSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { ICON_ORANGE_COLOR, LIVE_TV } from "~/utils/constant";

interface LiveDataCardProps {
  data: LiveData;
  selectedTab: string;
  selectedMedia?: string;
  handleOnTVClick: (type: string) => void;
}

function LiveDataCard({ data, selectedTab, selectedMedia, handleOnTVClick }: LiveDataCardProps) {
  const { currentAudio, isPlaying } = useAppSelector((state) => state.nityNiyam);
  const isMobile = useIsMobile();
  const { isVideoPlay } = useAppSelector((state) => state.youtubeVideo);
  const dispatch = useAppDispatch();
  const { bold, medium } = useFontStyle();
  const { pathname } = useLocation();
  const isLive = pathname.split("/")[2] === "live";
  const { t } = useTranslation();
  const { LocalizedLink } = useLocale();

  const { removeVideoPlay } = useStopCurrentRunningVideoAudio();

  const name = selectedTab === LIVE_TV ? data.tv_title : data.radio_title;
  const thumbnail = selectedTab === LIVE_TV ? getYoutubeVideoThumbnail(data.yt_id, "medium") : `./images/${data.type}.png`;

  const isTVActive = selectedMedia?.split("-").join("_") === data.type && isVideoPlay;
  const isRadioActive = currentAudio?.from === "live" && currentAudio.id === data.id && isPlaying;

  const handleClickOnTv = () => {
    handleOnTVClick(data.type);
    dispatch(onPlayVideo(data.yt_id));
    dispatch(changeIsAudioPlayState(false));
    dispatch(changeIsPlayingState(false));
    removeAudioPlayerKeyFromSession();
    handleRadioPauseButtonClick();
  };

  const handleOnRadioClick = () => {
    dispatch(onPauseVideo());
    removeVideoPlay();

    const currentAudio = {
      id: data.id,
      img: null,
      totalDuration: null,
      artist: null,
      name: data.radio_title,
      audio: data.radio_url,
      from: "live",
      isLive: true,
    };

    dispatch(changeIsAudioPlayState(true));
    dispatch(changeIsPlayingState(true));
    dispatch(addToCurrentAudio(currentAudio));
    dispatch(removeQueue());
    setMediaTypeControl("music");
  };

  const handleRadioPauseButtonClick = () => {
    dispatch(changeIsPlayingState(false));
  };

  const commonButtonClass = `cursor-pointer bg-cardBg border border-textHeadingColor rounded-full flex flex-row justify-center items-center gap-2 lg:px-2 2xl:px-3 py-1.5 2xl:py-2 ${
    isMobile ? "w-[90px]" : " w-[80px] 2xl:w-[110px]"
  }`;

  return (
    <div className="w-full">
      <div
        // to={`/live/${data.type.split("_").join("-")}`}
        className={cn(
          "w-full pb-[10px] flex flex-col justify-start items-center gap-2 cursor-pointer rounded-xl  overflow-hidden",
          isLive ? "bg-white/70" : "bg-cardBgColor",
        )}
        title={name}
        onMouseDown={() => handleClickOnTv()}
      >
        <div className="w-full aspect-[16/9] overflow-hidden rounded-t-[8px]">
          <img src={thumbnail} alt={data.type ?? "Live TV Thumbnail"} className="w-full h-full object-cover bg-gray-200 select-none" />
        </div>
        <div className="w-full flex flex-col justify-center items-center px-3">
          <span className={cn("line-clamp-1 leading-8 text-textColor body_text_m capitalize w-full text-center", medium)}>{name}</span>
        </div>
        <div className="flex justify-center items-center gap-3 sm:gap-2">
          <LocalizedLink
            to={`/live/${data.type.split("_").join("-")}`}
            className={commonButtonClass}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              isTVActive ? dispatch(onPauseVideo()) : handleClickOnTv();
            }}
            title={`Play ${data.tv_title}`}
          >
            <p>
              {isTVActive ? (
                <BsPauseFill size={18} color={ICON_ORANGE_COLOR} />
              ) : (
                <img
                  src="/svg/television.svg"
                  className="min-w-[20px] min-h-[20px] max-h-[20px] max-w-[20px]"
                  width={20}
                  height={20}
                  alt="Television"
                />
              )}
            </p>
            <p className={cn("text-textColor text-xs 2xl:text-sm pt-px", isMobile ? "" : "hidden md:block", medium)}>
              {t("live_tv.tv", { defaultValue: "TV" })}
            </p>
          </LocalizedLink>

          {data.radio_url && (
            <LocalizedLink
              to={`/live/${data.type.split("_").join("-")}`}
              className={commonButtonClass}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                isRadioActive ? handleRadioPauseButtonClick() : handleOnRadioClick();
              }}
              title={`Play ${data.radio_title}`}
            >
              <p>
                {/* {isRadioActive ? <BsPauseFill size={18} color={ICON_ORANGE_COLOR} /> : <PiRadioLight size={20} color={PLAY_PAUSE_ICON_COLOR} />} */}
                {isRadioActive ? (
                  <BsPauseFill size={18} color={ICON_ORANGE_COLOR} />
                ) : (
                  <img src="/svg/radio.svg" className="min-w-[20px] min-h-[20px] max-h-[20px] max-w-[20px]" width={20} height={20} alt="Radio" />
                )}
              </p>
              <p className={cn("text-textColor text-xs 2xl:text-sm pt-px ", isMobile ? "" : "hidden md:block", medium)}>
                {t("live_tv.radio", { defaultValue: "Radio" })}
              </p>
            </LocalizedLink>
          )}
        </div>
      </div>
    </div>
  );
}

export default LiveDataCard;
